import { useEffect, useRef } from "react";
import type { HkTableState } from "../gen/bazivan/v1/table_pb";
import { signal, useSignal, type Signal } from "../net/signal";
import { HaftScene } from "../haft/scene";
import { HaftOnline } from "../haft/controller";
import { HaftResults } from "./HaftResults";
import { BreakNext } from "./BreakNext";
import { nextLabel } from "./nextLabel";
import { fa } from "./cards";
import { botBare } from "./seats";
import { BotIcon } from "../ui/BotIcon";
import { avatarSrc } from "./avatar";
import { useDealSheet } from "./DealProof";

/**
 * میزِ هفت خبیث — ظرفِ ری‌اکتیِ صحنه‌ی امری.
 *
 * صحنه (HaftScene) و کنترلر (HaftOnline) پورتِ GameView و HaftOnlineController
 * یونیتی‌اند و DOM را خودشان می‌کشند؛ این کامپوننت فقط ظرف را می‌دهد و جدولِ
 * نتایجِ پایانِ دور را (ShowRoundOver). وضعیتِ سرور مستقیم به این کامپوننت
 * نمی‌رسد — از صفِ کنترلر می‌گذرد، تا انیمیشنِ قبلی تمام شده باشد.
 */
export function HaftTable({ state }: { state: HkTableState }) {
  const host = useRef<HTMLDivElement>(null);
  const sig = useRef<Signal<HaftOnline | null> | null>(null);
  if (!sig.current) sig.current = signal<HaftOnline | null>(null);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    let c: HaftOnline | null = null;
    const scene = new HaftScene(el, state.seats.length || 4, {
      onCard: (i) => c?.clickCard(i),
      onDraw: () => c?.draw(),
      onPickSuit: (s) => c?.pickSuit(s),
      onSeat: (l) => c?.seatClick(l),
      onPickOffered: (i) => c?.pickOffered(i),
      onCallUno: () => c?.callUno(),
      onCatch: () => c?.catchUno(),
      seatName: (l) => c?.name(l) ?? "حریف",
    });
    c = new HaftOnline(scene);
    sig.current!.value = c;
    c.start();
    const ro = new ResizeObserver(() => scene.relayout());
    ro.observe(el);
    return () => { ro.disconnect(); c?.dispose(); scene.destroy(); sig.current!.value = null; };
    // صحنه یک بار ساخته می‌شود؛ وضعیت‌های بعدی از صفِ کنترلر می‌آیند.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="table tv-host haft" ref={host}>
      <Chrome sig={sig.current} />
    </div>
  );
}

function Chrome({ sig }: { sig: Signal<HaftOnline | null> }) {
  const c = useSignal(sig);
  if (!c) return null;
  return <ChromeInner c={c} />;
}

function ChromeInner({ c }: { c: HaftOnline }) {
  const over = useSignal(c.overlay);
  const raw = useSignal(c.raw);
  const proof = useSignal(c.proof);
  const deal = useDealSheet((l) => c.name(l));
  // نتایج: هم پایانِ دور (خبرِ گذرا تا دستِ بعد) و هم پایانِ مسابقه. برگه جدا از
  // پنل می‌مانَد تا با شروعِ دورِ بعد بسته نشود.
  return (
    <>
      {over && raw && <HaftResults state={raw} extra={deal.chip(proof)}
        next={<BreakNext watch={c.brk} label={nextLabel("دور", raw.watching ? "even" : raw.winner === raw.me ? "win" : "lose")}
                         onNext={() => c.nextRound()} />} />}
      {deal.sheet}
    </>
  );
}

/** از این سهمِ سقف به بعد میله کهربایی و بعد سرخ می‌شود — «نزدیکِ باختی». */
const WARM = 0.5;
const HOT = 0.8;

/** رنگِ خطرِ هر بازیکن از سهمِ جریمه‌اش از سقف. */
export function haftRisk(points: number, target: number): "calm" | "warm" | "hot" {
  const r = points / Math.max(1, target);
  return r >= HOT ? "hot" : r >= WARM ? "warm" : "calm";
}

/** صدرنشین: تنها کسی که کم‌ترین جریمه را دارد — تا کسی جریمه نگرفته یا هم‌ترازند، هیچ‌کس. */
export function haftLeader(scores: number[]): number {
  if (scores.length === 0) return -1;
  const low = Math.min(...scores);
  if (Math.max(...scores) === low) return -1;
  return scores.filter((v) => v === low).length === 1 ? scores.indexOf(low) : -1;
}

/**
 * نوارِ بالا: جریمه‌ی تجمعیِ هر کس تا سقف — کم‌تر بهتر.
 *
 * پیش‌تر چهار ردیفِ میله‌ی بلند بود (همان واژگانِ چهاربرگ): نام در ستونی ۳۴ پیکسلی دوخطه می‌شد یا
 * روی میله می‌افتاد، میله‌ها روی دسکتاپ تا دو لبه‌ی قاب کشیده بودند و هیچ‌چیز نمی‌گفت کی جلوست و کی
 * لبِ سقف (کاربر، ۲۰ اکتبر ۲۰۲۶: «طراحیِ بهتری برای این قسمت می‌خواهم»). حالا هر بازیکن یک کارت
 * است: چهره و نام مثلِ صندلیِ میز، جریمه‌ی درشت، و میله‌ای کوتاه که با نزدیک‌شدن به سقف سبز ←
 * کهربایی ← سرخ می‌شود. تاجِ صدرنشین، کارتِ خودم طلایی و کارتِ نوبت روشن. دسکتاپ یک ردیف، گوشی
 * دوتادوتا — قدِ نوار کم‌تر می‌شود و میز بزرگ‌تر.
 */
export function HaftScorebar({ state }: { state: HkTableState }) {
  const scores = state.scores.map(Number);
  const target = state.targetScore;
  const lead = haftLeader(scores);
  const live = !state.roundOver && !state.matchOver;
  return (
    <div className="scorebar hk-board">
      <div className="deckmedal" title="کارت‌های مانده در دسته">
        <span className="s">{fa(state.deckLeft)}</span>
        <span className="l">دسته</span>
      </div>
      <ol className="hk-standings" data-n={scores.length}>
        {scores.map((v, u) => {
          const mine = !state.watching && u === state.me;
          const bot = !mine && !!state.seats[u]?.bot;
          const name = mine ? "تو" : botBare(state.seats[u]?.name || "بازیکن", bot);
          const cls = ["hk-p", haftRisk(v, target), mine && "me", live && u === state.turn && "turn", u === lead && "lead"];
          return (
            <li key={u} className={cls.filter(Boolean).join(" ")}
                aria-label={`${name}: ${fa(v)} از ${fa(target)}${u === lead ? "، کم‌ترین جریمه" : ""}`}>
              <img className="hk-face" src={avatarSrc(state.seats[u]?.avatar)} alt="" width={28} height={28} decoding="async" />
              <span className="hk-name" title={name}>
                {u === lead && (
                  <svg className="hk-crown" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M3 8l4.5 3.5L12 5l4.5 6.5L21 8l-2 10H5L3 8z" />
                  </svg>
                )}
                <span>{bot && <BotIcon size={12} />}{name}</span>
              </span>
              {/* کلید با مقدار: هر جریمه‌ی تازه عدد را دوباره می‌نشاند و همان یک بار می‌جهد */}
              <b className="hk-pts" key={v}>{fa(v)}</b>
              <span className="hk-meter" aria-hidden="true">
                <i style={{ inlineSize: `${Math.min(100, (v / Math.max(1, target)) * 100)}%` }} />
              </span>
            </li>
          );
        })}
      </ol>
      <div className={"deckmedal hk-cap" + (state.evil ? " evil" : "")} title={state.evil ? "سقف جریمه · حالت خبیث" : "سقف جریمه"}>
        <span className="s">{fa(target)}</span>
        <span className="l">{state.evil ? "سقف · خبیث" : "سقف"}</span>
      </div>
    </div>
  );
}
